import { type ReactNode, useState } from "react";
import { MenuItem, Button, TextField } from "@mui/material";
import { MapPinLineIcon, CaretDownIcon } from "@phosphor-icons/react";
import { useFormik } from "formik";
import dayjs from "dayjs";
import { useTranslations } from "use-intl";

import DestinationSearch from "./DestinationSearch";
import CheckInSelection from "./CheckInSelection";
import VisitorsSelection from "./VisitorsSelections";
import { TravelPurpose } from "@/app/lib/types/enums";
import { initialHotelsValues } from "@/app/lib/initialHotelsValues";
import { getValidationSchema } from "./validation.ts";
import { type THotelFormValues } from "@/app/lib/types/types";
import { Drawer, DrawerTopBar } from "../Drawer.tsx";
import { useSnackbarFormikErrors } from "@/app/lib/hooks/useSnackbarFormikErrors.ts";
import { getFieldError } from "@/app/lib/validation.ts";
import { usePreviousHotelSearchData } from "@/app/lib/hooks/usePreviousSearchData.ts";
import { ResetButton } from "../ResetButton.tsx";

enum DrawerComponent {
  DestinationSearch = "DestinationSearch",
  CheckInSelection = "CheckInSelection",
  VisitorsSelection = "VisitorsSelection",
}

type Props = {
  submitButtonContent: ReactNode;
  initialValues?: THotelFormValues | null;
  showErrorsInline?: boolean;
  onSubmit: (submittedValues: THotelFormValues) => void;
};

export const HotelsCalculator = ({
  submitButtonContent,
  initialValues,
  showErrorsInline,
  onSubmit,
}: Props) => {
  const [drawerComponent, setDrawerComponent] =
    useState<DrawerComponent | null>(null);
  const { previousSearchData } = usePreviousHotelSearchData();

  const t = useTranslations();

  const formik = useFormik({
    initialValues: initialValues || previousSearchData,
    validationSchema: getValidationSchema(t),
    validateOnChange: Boolean(showErrorsInline),
    validateOnBlur: Boolean(showErrorsInline),
    onSubmit: (values) => {
      onSubmit(values);
    },
  });

  useSnackbarFormikErrors<THotelFormValues>({
    disabled: showErrorsInline,
    errors: formik.errors,
    touched: formik.touched,
  });

  const checkInValue = dayjs(formik.values.dates.checkIn).format("ddd, D MMM");
  const checkOutValue = formik.values.dates.checkOut
    ? dayjs(formik.values.dates.checkOut).format("ddd, D MMM")
    : "_";

  const travelPurposeOptions = [
    {
      value: TravelPurpose.LEISURE,
      label: t("hotels.search.leisure"),
    },
    {
      value: TravelPurpose.BUSINESS,
      label: t("hotels.search.business"),
    },
  ];

  return (
    <>
      <form
        className="w-full"
        noValidate
        onSubmit={formik.handleSubmit}
        acceptCharset="ISO-8859-1"
      >
        <div className="mb-4 flex flex-col gap-2">
          <div className="relative flex flex-col gap-2">
            <TextField
              fullWidth
              value={formik.values.destination?.name || ""}
              error={
                showErrorsInline && !!getFieldError(formik, "destination.name")
              }
              helperText={
                showErrorsInline && getFieldError(formik, "destination.name")
              }
              name="destination.name"
              label={
                <div className="flex items-center gap-2">
                  <MapPinLineIcon size={24} />
                  <span>{t("hotels.search.destination")}</span>
                </div>
              }
              onClick={() => {
                setDrawerComponent(DrawerComponent.DestinationSearch);
              }}
              slotProps={{
                input: {
                  endAdornment: formik.values.destination ? (
                    <ResetButton
                      onClick={() => {
                        formik.setFieldValue(
                          "destination",
                          initialHotelsValues.destination,
                        );
                      }}
                      aria-label={t("hotels.search.resetDestination")}
                    />
                  ) : null,
                },
              }}
            />
          </div>
          <TextField
            fullWidth
            name="dates"
            label={t("hotels.search.checkInAndCheckOut")}
            value={`${checkInValue} - ${checkOutValue}`}
            onClick={() => {
              setDrawerComponent(DrawerComponent.CheckInSelection);
            }}
          />
          <TextField
            fullWidth
            name="rooms"
            label={t("hotels.search.visitors")}
            value={t("hotels.search.visitorsSumWithTypes", {
              adultsCount: formik.values.rooms.reduce(
                (acc, room) => acc + room.adults,
                0,
              ),
              childrenCount: formik.values.rooms.reduce(
                (acc, room) => acc + room.children,
                0,
              ),
            })}
            slotProps={{
              input: {
                endAdornment: (
                  <CaretDownIcon
                    className="shrink-0"
                    color="black"
                    size={20}
                    weight="bold"
                  />
                ),
              },
            }}
            onClick={(e) => {
              e.preventDefault();
              setDrawerComponent(DrawerComponent.VisitorsSelection);
            }}
          />
          <TextField
            select
            fullWidth
            name="travelPurpose"
            label={t("hotels.search.travelPurpose")}
            value={formik.values.travelPurpose}
            slotProps={{
              input: {
                endAdornment: (
                  <CaretDownIcon
                    className="shrink-0"
                    color="black"
                    size={20}
                    weight="bold"
                  />
                ),
              },
            }}
            onChange={(e) => {
              formik.setFieldValue("travelPurpose", e.target.value);
            }}
          >
            {travelPurposeOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        </div>
        <Button fullWidth variant="contained" type="submit">
          {submitButtonContent}
        </Button>
      </form>

      {/* we're not using open prop and must mount fresh instance of the component
      because otherwise initial opening animation breaks scroll when mobile keyboard is present */}
      {drawerComponent ? (
        <Drawer
          hasFullHeight={
            !!drawerComponent &&
            drawerComponent !== DrawerComponent.VisitorsSelection
          }
          onClose={() => setDrawerComponent(null)}
          open
          disableRestoreFocus
        >
          {drawerComponent === DrawerComponent.DestinationSearch && (
            <>
              <DrawerTopBar
                onCloseClick={() => setDrawerComponent(null)}
                title={t("hotels.search.destinationSelect")}
              />
              <DestinationSearch
                formProps={formik}
                onClose={() => setDrawerComponent(null)}
              />
            </>
          )}
          {drawerComponent === DrawerComponent.CheckInSelection && (
            <>
              <DrawerTopBar
                onCloseClick={() => setDrawerComponent(null)}
                title={t("hotels.search.checkInAndCheckOut")}
              />
              <CheckInSelection
                formProps={formik}
                onClose={() => setDrawerComponent(null)}
              />
            </>
          )}
          {drawerComponent === DrawerComponent.VisitorsSelection && (
            <>
              <DrawerTopBar
                onCloseClick={() => setDrawerComponent(null)}
                title={t("hotels.search.visitors")}
              />
              <VisitorsSelection
                formProps={formik}
                onClose={() => setDrawerComponent(null)}
              />
            </>
          )}
        </Drawer>
      ) : null}
    </>
  );
};
